import { mdiDownload, mdiFileDocument } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useRef, useState } from "react";
import Papa from "papaparse";

interface ExtraItemBulkUploadResult {
  successCount: number;
  failedCount: number;
  successRows: Array<{
    warehouse_id: string;
    item_code: string;
    qty: number;
    row: number;
  }>;
  failedRows: Array<{
    warehouse_id: string;
    item_code: string;
    qty: number;
    row: number;
    error: string;
  }>;
}

interface ExtraItemBulkUploadProps {
  onUploadComplete: (results: ExtraItemBulkUploadResult) => void;
  onClose: () => void;
  validateAndFetchData: (
    warehouse_id: string,
    item_code: string,
    qty: number,
  ) => Promise<{ success: boolean; data?: any; error?: string }>;
  id_prefix?: string;
}

const ExtraItemBulkUpload: React.FC<ExtraItemBulkUploadProps> = ({
  onUploadComplete,
  onClose,
  validateAndFetchData,
  id_prefix = "extra-item-bulk-upload",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] =
    useState<ExtraItemBulkUploadResult | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const downloadTemplate = () => {
    const csvContent = "warehouse_id,item_code,qty";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_upload_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.name.endsWith(".csv")) {
      alert("Please upload a CSV file");
      return;
    }

    setUploading(true);
    setUploadResult(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        await processCSVData(results.data);
      },
      error: (error) => {
        console.error("CSV parsing error:", error);
        alert("Error parsing CSV file");
        setUploading(false);
      },
    });
  };

  const processCSVData = async (data: any[]) => {
    const successRows: ExtraItemBulkUploadResult["successRows"] = [];
    const failedRows: ExtraItemBulkUploadResult["failedRows"] = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rowNumber = i + 2; // +2 because of header row and 0-indexing

      // Validate required fields
      if (!row.warehouse_id || !row.item_code || !row.qty) {
        failedRows.push({
          warehouse_id: row.warehouse_id || "",
          item_code: row.item_code || "",
          qty: row.qty || 0,
          row: rowNumber,
          error: "Missing required fields (warehouse_id, item_code, or qty)",
        });
        continue;
      }

      // Validate qty is a number
      const qty = parseFloat(row.qty);
      if (isNaN(qty) || qty <= 0) {
        failedRows.push({
          warehouse_id: row.warehouse_id,
          item_code: row.item_code,
          qty: row.qty,
          row: rowNumber,
          error: "Quantity must be a positive number",
        });
        continue;
      }

      // Validate and fetch data from backend
      try {
        const result = await validateAndFetchData(
          row.warehouse_id.trim(),
          row.item_code.trim(),
          qty,
        );

        if (result.success) {
          successRows.push({
            warehouse_id: row.warehouse_id.trim(),
            item_code: row.item_code.trim(),
            qty: qty,
            row: rowNumber,
          });
        } else {
          failedRows.push({
            warehouse_id: row.warehouse_id.trim(),
            item_code: row.item_code.trim(),
            qty: qty,
            row: rowNumber,
            error: result.error || "Validation failed",
          });
        }
      } catch (error: any) {
        failedRows.push({
          warehouse_id: row.warehouse_id.trim(),
          item_code: row.item_code.trim(),
          qty: qty,
          row: rowNumber,
          error: error.message || "Unknown error occurred",
        });
      }
    }

    const result: ExtraItemBulkUploadResult = {
      successCount: successRows.length,
      failedCount: failedRows.length,
      successRows,
      failedRows,
    };

    setUploadResult(result);
    setUploading(false);
  };

  const handleConfirm = () => {
    if (uploadResult && uploadResult.successCount > 0) {
      onUploadComplete(uploadResult);
      onClose();
    }
  };

  return (
    <div
      id={`${id_prefix}-modal`}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2
            id={`${id_prefix}-title`}
            className="text-xl font-semibold text-gray-800"
          >
            Bulk Upload Items
          </h2>
          <button
            id={`${id_prefix}-close-btn`}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {!uploadResult ? (
            <div className="space-y-6">
              {/* Instructions */}
              {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">
                  Instructions:
                </h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                  <li>Download the CSV template below</li>
                  <li>Fill in warehouse_id, item_code, and qty columns</li>
                  <li>Upload the completed CSV file</li>
                  <li>Review the results and confirm</li>
                </ol>
              </div> */}

              {/* Template Download */}
              <div className="flex justify-center">
                <button
                  id={`${id_prefix}-download-template-btn`}
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 bg-myPrimaryColor text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors shadow-md"
                >
                  <Icon path={mdiDownload} className="h-5 w-5" />
                  Download Template
                </button>
              </div>

              {/* Upload Area */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  dragActive
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 bg-gray-50"
                }`}
              >
                <input
                  id={`${id_prefix}-file-input`}
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Icon
                  path={mdiFileDocument}
                  className="h-16 w-16 mx-auto text-gray-400 mb-4"
                />
                <p className="text-gray-600 mb-2">
                  Drag and drop your CSV file here, or
                </p>
                <button
                  id={`${id_prefix}-browse-btn`}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-myPrimaryColor text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? "Processing..." : "Browse Files"}
                </button>
              </div>

              {uploading && (
                <div className="flex items-center justify-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600">Processing CSV file...</span>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-700 mb-1">Success</p>
                  <p
                    id={`${id_prefix}-success-count`}
                    className="text-3xl font-bold text-green-900"
                  >
                    {uploadResult.successCount}
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700 mb-1">Failed</p>
                  <p
                    id={`${id_prefix}-failed-count`}
                    className="text-3xl font-bold text-red-900"
                  >
                    {uploadResult.failedCount}
                  </p>
                </div>
              </div>

              {/* Failed Rows */}
              {uploadResult.failedCount > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-red-900">Failed Rows:</h3>
                  <div className="max-h-64 overflow-auto border border-red-200 rounded-lg">
                    <table className="min-w-full divide-y divide-red-200">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Row
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Warehouse ID
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Item Code
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Qty
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-red-700">
                            Error
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-red-100">
                        {uploadResult.failedRows.map((row, index) => (
                          <tr
                            key={index}
                            id={`${id_prefix}-failed-row-${index}`}
                            className="hover:bg-red-50"
                          >
                            <td className="px-4 py-2 text-sm">{row.row}</td>
                            <td className="px-4 py-2 text-sm">
                              {row.warehouse_id}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {row.item_code}
                            </td>
                            <td className="px-4 py-2 text-sm">{row.qty}</td>
                            <td className="px-4 py-2 text-sm text-red-600">
                              {row.error}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Success Rows Preview */}
              {uploadResult.successCount > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-green-900">
                    Successfully Validated Rows ({uploadResult.successCount}):
                  </h3>
                  <div className="max-h-64 overflow-auto border border-green-200 rounded-lg">
                    <table className="min-w-full divide-y divide-green-200">
                      <thead className="bg-green-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Row
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Warehouse ID
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Item Code
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-green-700">
                            Qty
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-green-100">
                        {uploadResult.successRows.map((row, index) => (
                          <tr
                            key={index}
                            id={`${id_prefix}-success-row-${index}`}
                            className="hover:bg-green-50"
                          >
                            <td className="px-4 py-2 text-sm">{row.row}</td>
                            <td className="px-4 py-2 text-sm">
                              {row.warehouse_id}
                            </td>
                            <td className="px-4 py-2 text-sm">
                              {row.item_code}
                            </td>
                            <td className="px-4 py-2 text-sm">{row.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          {uploadResult && (
            <>
              <button
                id={`${id_prefix}-upload-again-btn`}
                onClick={() => setUploadResult(null)}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Upload Again
              </button>
              <button
                id={`${id_prefix}-confirm-btn`}
                onClick={handleConfirm}
                disabled={uploadResult.successCount === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm & Add {uploadResult.successCount} Items
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExtraItemBulkUpload;
